import { buildMrpSkuDetailPath, type MrpAmlEntry } from "@/lib/mrp";

/** Forecast-only placeholder before real AML alternates exist — not a pack-size row. */
export const MRP_FORECAST_PLACEHOLDER_ITEM_SOURCE = "mrp_forecast_placeholder";

/** Approval lifecycle. Orthogonal to the Active listing flag. */
export const AML_STATUS_COLUMN_HINT =
  "Approval lifecycle for this manufacturer alternate: pending review, approved, rejected, or deprecated. Deprecated means this part is no longer approved for the CPN. Status is not the Active listing flag.";

/** Listing flag only. Orthogonal to Status. */
export const AML_ACTIVE_COLUMN_HINT =
  "Listing flag only. Yes shows the line on the default AML list. No hides it there. If Status is still approved, shopping and sourcing still treat this as an approved part. Inactive is not end of life. To un-approve, set Status to deprecated and an end date.";

export const AML_SHOW_INACTIVE_HINT =
  "When off, the list is active rows with Status approved. When on, the list also includes inactive listing rows and other statuses (pending review, rejected, deprecated). Inactive does not mean un-approved.";

export const AML_END_DATE_COLUMN_HINT =
  "Inclusive last valid date for this alternate. Set together with Status deprecated when a part is no longer approved.";

export const AML_DEACTIVATE_ACTION_HINT =
  "Deactivate only sets Active to no (hides from the default list). It does not un-approve the part. Shopping still shows inactive approved lines. To end approval, set Status to deprecated and an end date.";

export function formatAmlEndDate(value: string | null | undefined): string {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).slice(0, 10);
}

export function isForecastPlaceholderAmlRow(row: MrpAmlEntry): boolean {
  return row.item_source === MRP_FORECAST_PLACEHOLDER_ITEM_SOURCE;
}

export function isAmlSelfAlternateRow(row: MrpAmlEntry): boolean {
  if (isForecastPlaceholderAmlRow(row)) return false;
  return (row.manufacturer_name?.trim() ?? "") === "" && (row.mpn?.trim() ?? "") === "";
}

export function isPackSizeAmlRow(row: MrpAmlEntry): boolean {
  return !isForecastPlaceholderAmlRow(row);
}

export function formatAmlAlternateLabel(row: MrpAmlEntry, cpn: string): string {
  if (isAmlSelfAlternateRow(row)) {
    return `Placeholder until first AML line (${cpn})`;
  }
  const manufacturer = row.manufacturer_name?.trim();
  if (manufacturer) {
    const mpn = row.mpn?.trim();
    return mpn ? `${manufacturer} · ${mpn}` : manufacturer;
  }
  return row.mpn?.trim() ? row.mpn.trim() : "No manufacturer";
}

export function amlChangeRequestActionLabel(action: string): string {
  if (action === "create") return "New line";
  if (action === "notes") return "Notes";
  if (action === "activate") return "Activate";
  if (action === "deactivate") return "Deactivate";
  if (action === "delete") return "Delete";
  return action;
}

export function summarizeAmlChangeRequestAction(
  action: string,
  payload:
    | {
        notes?: string | null;
        manufacturer_name?: string | null;
        mpn?: string | null;
        item_source?: string | null;
      }
    | null
    | undefined,
): string {
  if (action === "create") {
    const parts = [
      payload?.manufacturer_name?.trim(),
      payload?.mpn?.trim(),
    ].filter((part): part is string => Boolean(part && part.length > 0));
    if (payload?.item_source?.trim()) {
      parts.push(`source: ${payload.item_source.trim()}`);
    }
    if (payload?.notes?.trim()) {
      parts.push(`notes: ${payload.notes.trim()}`);
    }
    return parts.length > 0 ? parts.join(" · ") : "New AML line";
  }
  if (action === "notes") {
    const n = payload?.notes;
    if (n === null || n === "") return "Clear notes";
    return n ?? "—";
  }
  if (action === "activate") return "Show on the default AML list (listing flag only)";
  if (action === "deactivate") return "Hide from the default AML list (does not change Status)";
  if (action === "delete") return "Remove this AML line";
  return action;
}

/** Same ordering as grouped AML rows (manufacturer, MPN, id). */
export function sortAmlAlternateRows(rows: MrpAmlEntry[]): MrpAmlEntry[] {
  return [...rows].sort((a, b) => {
    const mfg = (a.manufacturer_name ?? "").localeCompare(b.manufacturer_name ?? "");
    if (mfg !== 0) return mfg;
    const mpn = (a.mpn ?? "").localeCompare(b.mpn ?? "");
    if (mpn !== 0) return mpn;
    return a.id - b.id;
  });
}

/** Deep link to AML alternates and history on the SKU detail page (not a separate AML route). */
export function amlCpnDetailPath(cpn: string): string {
  return buildMrpSkuDetailPath(cpn, { tab: "aml" });
}

export function formatSupportingDocsLine(value: MrpAmlEntry["supporting_docs"]): string | null {
  if (value == null) return null;
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.map((entry) => String(entry)).join(", ");
}

export function formatAmlMpnAliasesLine(value: MrpAmlEntry["mpn_aliases"]): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const aliases = value.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  if (aliases.length === 0) return null;
  return aliases.join(", ");
}

export function amlMpnAliasesDraftFromRow(row: MrpAmlEntry): string {
  return (row.mpn_aliases ?? []).join("\n");
}

export function parseAmlMpnAliasesDraft(raw: string): string[] {
  const seen = new Set<string>();
  const aliases: string[] = [];
  for (const part of raw.split(/[\n,]+/u)) {
    const trimmed = part.trim();
    if (trimmed.length === 0) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    aliases.push(trimmed);
  }
  return aliases;
}

/** Native title text for one AML row (source, updated, request metadata). */
export function buildAmlCpnHoverHint(row: MrpAmlEntry): string {
  const lines: string[] = [];
  const updated = new Date(row.updated_at);
  lines.push(
    `Updated: ${Number.isNaN(updated.getTime()) ? row.updated_at : updated.toLocaleString()}`,
  );
  const itemSource = row.item_source?.trim();
  if (itemSource) lines.push(`Source: ${itemSource}`);
  const source = row.request_source?.trim();
  if (source) lines.push(`Request source: ${source}`);
  const context = row.request_context?.trim();
  if (context) lines.push(`Request context: ${context}`);
  if (row.requested_at) {
    const requestedAt = new Date(row.requested_at);
    lines.push(
      `Requested at: ${Number.isNaN(requestedAt.getTime()) ? row.requested_at : requestedAt.toLocaleString()}`,
    );
  }
  if (row.requested_by != null && String(row.requested_by).trim() !== "") {
    lines.push(`Requested by id: ${row.requested_by}`);
  }
  const docs = formatSupportingDocsLine(row.supporting_docs);
  if (docs) lines.push(`Supporting docs: ${docs}`);
  return lines.join("\n");
}

export function buildGroupedCpnHoverHint(rows: MrpAmlEntry[]): string {
  if (rows.length === 1) return buildAmlCpnHoverHint(rows[0]);
  return [`${rows.length} AML lines share this CPN.`, "", ...rows.map((r) => buildAmlCpnHoverHint(r))].join("\n");
}

export function groupAmlEntriesByCpn(rows: MrpAmlEntry[]): { cpn: string; rows: MrpAmlEntry[] }[] {
  const map = new Map<string, MrpAmlEntry[]>();
  for (const row of rows) {
    const list = map.get(row.cpn) ?? [];
    list.push(row);
    map.set(row.cpn, list);
  }
  const groups = [...map.entries()].sort((first, second) => first[0].localeCompare(second[0]));
  return groups.map(([cpn, groupRows]) => ({
    cpn,
    rows: sortAmlAlternateRows(groupRows),
  }));
}
